import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Check, Zap, Award, Target, TrendingUp, ChevronRight, DollarSign, Percent, Users } from 'lucide-react';
import TopNavbar from '../components/TopNavbar';
import MobileNavbar from '../components/MobileNavbar';
import Footer from '../components/Landing/Footer';
import { useNavigate } from 'react-router-dom';

const CreateDealPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const features = [
    {
      icon: <DollarSign className="text-[#FF6C6C]" />,
      title: "Increase Revenue",
      description: "Boost your sales through targeted deals and promotional offers that attract new customers."
    },
    {
      icon: <Users className="text-[#FF6C6C]" />,
      title: "Expand Customer Base",
      description: "Reach thousands of potential customers actively looking for deals in your industry."
    },
    {
      icon: <TrendingUp className="text-[#FF6C6C]" />,
      title: "Track Performance",
      description: "Access detailed analytics to measure the success of your promotional campaigns."
    },
    {
      icon: <Percent className="text-[#FF6C6C]" />,
      title: "Flexible Discounts",
      description: "Create custom deals with the exact discount structure that works for your business model."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      business: "Cafe Delight",
      image: "https://randomuser.me/api/portraits/women/62.jpg",
      quote: "Since joining Dealboxx, we've seen a 40% increase in new customers. The platform is incredibly easy to use!"
    },
    {
      name: "Michael Thomas",
      business: "Tech Solutions",
      image: "https://randomuser.me/api/portraits/men/32.jpg",
      quote: "Creating and managing deals is seamless. Our promotional offers reach people we couldn't access before."
    },
    {
      name: "Rebecca Owusu",
      business: "Beauty Haven",
      image: "https://randomuser.me/api/portraits/women/45.jpg",
      quote: "The ROI we've seen through Dealboxx is incredible. It's now a core part of our marketing strategy."
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Create Your Deal",
      description: "Design attractive offers with our easy-to-use tools. Set prices, discounts, and terms in minutes."
    },
    {
      number: "02",
      title: "Publish & Promote",
      description: "Launch your deal to thousands of potential customers with a single click. We handle the promotion."
    },
    {
      number: "03",
      title: "Track & Convert",
      description: "Monitor performance in real-time and watch as new customers discover your business."
    },
    {
      number: "04",
      title: "Analyze & Optimize",
      description: "Use our powerful analytics to refine your strategy and maximize your ROI."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <TopNavbar isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      {isMenuOpen && (
        <MobileNavbar isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      )}
      
      {/* Hero Section */}
      <section className="overflow-hidden pt-28 pb-20">
        <div className="container px-4 mx-auto">
          <div className="flex flex-wrap items-center -mx-4">
            <div className="px-4 mb-16 w-full md:w-1/2 md:mb-0">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-xl"
              >
                <h1 className="mb-6 text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
                  Grow Your Business with <span className="text-[#FF6C6C]">Irresistible Deals</span>
                </h1>
                <p className="mb-8 text-lg text-gray-600">
                  Create and publish compelling deal offers that attract new customers and boost your revenue. Our platform makes it simple to design, launch, and track promotional campaigns.
                </p>
                <div className="flex flex-wrap gap-4">
                  <motion.button
                  onClick={()=>navigate("/signin")}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 font-medium text-white transition-all rounded-lg bg-gradient-to-r from-[#FF6C6C] to-[#FF8F6C] hover:shadow-lg"
                  >
                    Get Started Free
                  </motion.button>
                  <button onClick={()=>navigate("/support")} className="flex items-center px-8 py-4 font-medium transition-all rounded-lg text-[#FF6C6C] hover:bg-[#FF6C6C]/5">
                    Contact Us
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </button>
                </div>
                <div className="flex items-center mt-8 space-x-4">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4].map((num) => (
                      <div
                        key={num}
                        className="flex justify-center items-center w-10 h-10 rounded-full border-2 border-white shadow-lg backdrop-blur-sm bg-white/30"
                        style={{
                          background: `linear-gradient(135deg, rgba(255,108,108,${0.2 + (num * 0.1)}) 0%, rgba(255,143,108,${0.2 + (num * 0.1)}) 100%)`
                        }}
                      />
                    ))}
                  </div>
                  <div>
                    <div className="flex items-center">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} className="w-5 h-5 fill-[#FFD166] text-[#FFD166]" />
                      ))}
                    </div>
                    <p className="text-sm text-gray-600">Trusted by 500+ businesses</p>
                  </div>
                </div>
              </motion.div>
            </div>
            <div className="px-4 w-full md:w-1/2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <div className="overflow-hidden relative z-10 p-8 bg-white rounded-2xl shadow-xl">
                  <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-[#FF6C6C]/10 -mr-10 -mt-10"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-[#FFD166]/10 -ml-8 -mb-8"></div>
                  
                  <div className="relative">
                    <div className="mb-6 text-center">
                      <div className="inline-block p-2 mb-2 bg-[#FF6C6C]/10 rounded-lg">
                        <Award className="w-6 h-6 text-[#FF6C6C]" />
                      </div>
                      <h3 className="mb-1 text-2xl font-bold">Pro Plan</h3>
                      <p className="text-gray-600">Special launch discount</p>
                    </div>
                    
                    <div className="p-4 mb-6 bg-gray-50 rounded-lg">
                      <div className="absolute -top-2 -right-2 px-3 py-1.5 text-sm font-bold text-white rounded-full bg-[#FF6C6C] shadow-lg">
                        50% OFF
                      </div>
                      <div className="flex items-center mb-2">
                        <div className="w-16 h-16 mr-4 overflow-hidden rounded-lg bg-[#FFD166]/20">
                          <div className="flex justify-center items-center h-full">
                            <Percent className="w-8 h-8 text-[#FFD166]" />
                          </div>
                        </div>
                        <div>
                          <h4 className="text-lg font-bold">Pro Plan</h4>
                          <p className="text-sm text-gray-600">All-in-one solution for serious vendors</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center pt-4 mt-4 border-t border-gray-200">
                        <div>
                          <p className="text-gray-600 line-through">GHS 299.99/mo</p>
                          <p className="text-2xl font-bold text-[#FF6C6C]">GHS 199.99/mo</p>
                        </div>
                        <p className="px-3 py-1 text-sm font-medium text-white rounded-full bg-[#FF6C6C]">Save GHS 100</p>
                      </div>
                    </div>
                    
                    <ul className="mb-6 space-y-3">
                      {[
                        "35 deal listings",
                        "2900 Subscriber Leads",
                        "Advanced analytics dashboard",
                        "Promote 7 deals",
                        "5 Sender IDs",
                      ].map((item, index) => (
                        <li key={index} className="flex items-center">
                          <Check className="mr-3 w-5 h-5 text-green-500" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <button onClick={()=>navigate("/signin")} className="w-full px-6 py-3 font-medium text-white transition-all rounded-lg bg-[#FF6C6C] hover:bg-[#ff5252] shadow-md">
                      Get Offer Now
                    </button>
                    <p className="mt-3 text-xs text-center text-gray-500">Limited time offer • Get Started Now!</p>
                  </div>
                </div>
                
                <div className="absolute w-24 h-24 rounded-full bg-[#FF6C6C]/10 -right-4 top-1/3"></div>
                <div className="absolute w-16 h-16 rounded-full bg-[#FFD166]/10 -left-6 bottom-1/4"></div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container px-4 mx-auto">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-4 text-3xl font-bold md:text-4xl">Everything You Need to Succeed</h2>
              <p className="text-lg text-gray-600">
                Our platform provides all the tools required to create, manage, and optimize your promotional campaigns.
              </p>
            </motion.div>
          </div>
          
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white rounded-xl border border-gray-100 transition-all hover:shadow-lg"
              >
                <div className="p-4 mb-4 bg-gray-50 rounded-lg">
                  {feature.icon}
                </div>
                <h3 className="mb-3 text-xl font-bold">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="container px-4 mx-auto">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-4 text-3xl font-bold md:text-4xl">How It Works</h2>
              <p className="text-lg text-gray-600">
                Get your business in front of thousands of potential customers in just a few simple steps
              </p>
            </motion.div>
          </div>
          
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative p-6 bg-white rounded-xl"
              >
                <span className="block mb-4 text-3xl font-bold text-[#FF6C6C]/20">{step.number}</span>
                <h3 className="mb-3 text-xl font-bold">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
                {index < steps.length - 1 && (
                  <div className="hidden absolute -right-8 top-1/2 w-16 h-px bg-gray-200 transform rotate-12 lg:block"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container px-4 mx-auto">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-4 text-3xl font-bold md:text-4xl">What Our Vendors Say</h2>
              <p className="text-lg text-gray-600">
                Join thousands of businesses already growing with Dealboxx
              </p>
            </motion.div>
          </div>
          
          <div className="grid gap-8 md:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm"
              >
               
                <p className="italic text-gray-700">"{testimonial.quote}"</p>
                <div className="flex mt-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-4 h-4 fill-[#FFD166] text-[#FFD166]" />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#FF6C6C] to-[#FF8F6C] text-white">
        <div className="container px-4 mx-auto">
          <div className="mx-auto max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-6 text-3xl font-bold md:text-4xl">Ready to Boost Your Business?</h2>
              <p className="mb-8 text-lg">
                Join thousands of vendors already growing their customer base and increasing revenue with Dealboxx.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <motion.button
                onClick={()=>navigate("/signin")}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 font-medium text-[#FF6C6C] transition-all bg-white rounded-lg hover:shadow-lg"
                >
                  Sign Up Now
                </motion.button>
                <button onClick={()=>navigate("/support")} className="px-8 py-4 font-medium bg-white bg-opacity-20 rounded-lg transition-all hover:bg-opacity-30">
                  Contact Us
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default CreateDealPage; 